/**
 * La facture telle que l'écran de l'administration la voit.
 *
 * ## Le statut est CALCULÉ, jamais lu
 *
 * `settlement` vient de `v_invoice_balances`, qui le dérive des affectations à
 * chaque lecture. Ce n'est pas `invoices.status`, et la distinction est la
 * raison d'être de cet écran : mesuré le 10 septembre 2026, la colonne stockée
 * et le solde calculé se contredisent sur **2 116 factures sur 3 711 — 57 %**.
 *
 * | la colonne dit | le solde dit | factures |
 * | --- | --- | ---: |
 * | `pending` | soldée | 2 020 |
 * | `paid` | **encore due** | 44 |
 * | `pending` | partielle | 48 |
 *
 * La colonne stockée n'est pas exposée par `v_admin_factures` : un écran qui
 * ne peut pas la lire ne peut pas la réafficher par accident.
 */

/** Les cinq états qu'une facture peut avoir, calculés depuis les affectations. */
export type Settlement = 'paid' | 'partial' | 'unpaid' | 'cancelled' | 'credited';

export const SETTLEMENTS: readonly Settlement[] = [
  'paid', 'partial', 'unpaid', 'cancelled', 'credited',
] as const;

/**
 * Ce que chaque état veut dire, en français, et comment il s'affiche.
 *
 * `credited` a besoin de sa propre phrase : « annulée par notes de crédit »
 * n'est pas « annulée ». Une facture `cancelled` a été annulée par une
 * décision ; une facture `credited` réclame zéro parce que ses notes de crédit
 * couvrent son montant, sans que personne n'ait touché à son état. Confondre
 * les deux ferait croire qu'une décision a été prise là où seule
 * l'arithmétique a parlé.
 */
export const ETATS: Record<Settlement, { label: string; classe: string; phrase: string }> = {
  paid: {
    label: 'Soldée',
    classe: 'bg-green-100 text-green-800',
    phrase: 'Entièrement payée.',
  },
  partial: {
    label: 'Partielle',
    classe: 'bg-amber-100 text-amber-900',
    phrase: 'Une partie a été reçue ; le reste est dû.',
  },
  unpaid: {
    label: 'Impayée',
    classe: 'bg-red-100 text-red-800',
    phrase: 'Aucun paiement affecté à ce jour.',
  },
  cancelled: {
    label: 'Annulée',
    classe: 'bg-gray-200 text-gray-700',
    phrase: 'Annulée. Si elle réclame encore, c’est qu’aucune note de crédit ne l’a effacée.',
  },
  credited: {
    label: 'Créditée',
    classe: 'bg-indigo-100 text-indigo-800',
    phrase: 'Ses notes de crédit couvrent le montant : elle ne réclame plus rien.',
  },
};

/**
 * Les filtres de l'écran.
 *
 * Les cinq états, plus quatre filtres qui portent une QUESTION plutôt qu'un
 * état — et ce sont eux qui servent au travail réel :
 *
 * - `reclame` : ce qu'une famille doit encore. Il exclut les annulées, et
 *   c'est la correction du 10 septembre 2026 : il en comptait onze de trop.
 *   Une facture annulée ne réclame rien à personne — la décision d'annuler
 *   l'a éteinte pour la famille — même quand son solde reste inscrit. Le §0
 *   bis le dit d'une phrase : « Aucune de ces lignes n'appelle une action
 *   commerciale. » Un filtre qui les mélangeait envoyait relancer des gens
 *   dont la commande était annulée.
 * - `a_crediter` : les annulées dont le document est PARTI et dont le solde
 *   reste inscrit. Dix en production, 1 093,00 EUR — le stock que la règle D1
 *   doit reprendre, et la seule population qui appelle une note de crédit.
 * - `annulee_avant_envoi` : les annulées dont aucun document n'est jamais
 *   sorti. Une seule, 1,50 EUR. Rien à réclamer, rien à créditer (§5.6) —
 *   mais elle garde son filtre, parce que le §5.6 interdit aussi de faire
 *   disparaître la contradiction : on la nomme, on ne l'efface pas.
 * - `trop_percu` : les factures qui ont reçu plus que leur net. Elles
 *   apparaissent quand une note de crédit est émise sans déplacer l'argent
 *   qu'elle libère, ce que la passation appelle « un appel, pas deux ».
 */
export type Filtre =
  | 'tous' | Settlement
  | 'reclame' | 'a_crediter' | 'annulee_avant_envoi' | 'trop_percu';

export const FILTRES: readonly { valeur: Filtre; label: string }[] = [
  { valeur: 'tous', label: 'Toutes les factures' },
  { valeur: 'reclame', label: 'Réclame encore un paiement' },
  { valeur: 'unpaid', label: 'Impayées' },
  { valeur: 'partial', label: 'Partiellement payées' },
  { valeur: 'paid', label: 'Soldées' },
  { valeur: 'cancelled', label: 'Annulées' },
  { valeur: 'credited', label: 'Créditées' },
  { valeur: 'a_crediter', label: 'Annulées à créditer' },
  { valeur: 'annulee_avant_envoi', label: 'Annulées avant tout envoi' },
  { valeur: 'trop_percu', label: 'Trop-perçu' },
] as const;

/** Les tris proposés, et la colonne de la vue qui les porte. */
export type Tri = 'recentes' | 'anciennes' | 'reste_decroissant' | 'echeance' | 'famille';

export const TRIS: Record<Tri, { label: string; colonne: string; ascendant: boolean }> = {
  recentes: { label: 'Les plus récentes', colonne: 'created_at', ascendant: false },
  anciennes: { label: 'Les plus anciennes', colonne: 'created_at', ascendant: true },
  reste_decroissant: { label: 'Reste dû décroissant', colonne: 'outstanding_cents', ascendant: false },
  echeance: { label: 'Échéance la plus proche', colonne: 'due_date', ascendant: true },
  famille: { label: 'Famille (A→Z)', colonne: 'nom', ascendant: true },
};

/** Une ligne de l'écran, telle que `v_admin_factures` la rend. */
export interface LigneFacture {
  invoiceId: string;
  userId: string | null;
  invoiceNumber: string;
  creeLe: string;
  echeanceLe: string | null;
  settlement: Settlement;
  /** Le brut facturé. */
  factureCents: number;
  notesCents: number;
  notesCount: number;
  /** Brut moins notes de crédit : ce que la facture réclame vraiment. */
  netCents: number;
  recuCents: number;
  resteCents: number;
  /** Reçu au-delà du net. Voir le filtre `trop_percu`. */
  tropPercuCents: number;
  dernierPaiement: string | null;
  pdfUrl: string | null;
  communication: string | null;
  communicationStructuree: string | null;
  courrielEnvoyeLe: string | null;
  /** Le document est-il parti ? Condition du §5.6 avant toute note de crédit. */
  documentDelivre: boolean;
  nom: string | null;
  prenom: string | null;
  courriel: string | null;
}

/** Une note de crédit émise sur une facture. */
export interface NoteDeCredit {
  creditNoteId: string;
  invoiceId: string;
  numero: string;
  montantCents: number;
  emiseLe: string;
  pdfUrl: string | null;
  type: string | null;
  motif: string | null;
  notes: string | null;
  /**
   * `issued` tant que la note n'est pas partie, `sent` une fois le courriel
   * envoyé par `send-credit-note-email`. Les notes annulées ne sont pas lues.
   */
  statut: string | null;
}

/**
 * Un mouvement d'argent touchant une facture, lu dans le journal.
 *
 * `sens` distingue une entrée d'une dépense, et `methode` dit QUI l'a décidé —
 * la machine par une référence lue, ou un humain. C'est la contrepartie de
 * l'automatisme : on ne fait confiance qu'à ce qu'on peut vérifier.
 */
export interface Mouvement {
  ligneId: string;
  sens: 'entree' | 'depense';
  decideLe: string;
  dateValeur: string;
  montantCents: number;
  mouvementCents: number;
  cible: string;
  methode: string;
  communication: string | null;
  nomContrepartie: string | null;
  compteContrepartie: string | null;
  notes: string | null;
}

/** Un compte portant une provision, et la dette qu'il porte en même temps. */
export interface LigneProvision {
  userId: string;
  nom: string | null;
  prenom: string | null;
  courriel: string | null;
  provisionCents: number;
  mouvementCount: number;
  dernierMouvement: string | null;
  detteCents: number;
  facturesOuvertes: number;
  aReaffecterCents: number;
}

/** Le nom d'une famille, ou un repli qui ne prétend pas en être un. */
export function etiquetteFamille(nom?: string | null, prenom?: string | null): string {
  return [prenom, nom].filter(Boolean).join(' ').trim() || 'Compte sans nom';
}

/** Comment la méthode d'affectation se lit, en français. */
export const METHODES: Record<string, { label: string; classe: string }> = {
  structured: { label: 'communication structurée', classe: 'bg-green-100 text-green-800' },
  legacy_reference: { label: 'référence CDV lue', classe: 'bg-green-100 text-green-800' },
  manual: { label: 'décidé à la main', classe: 'bg-gray-100 text-gray-700' },
  bank: { label: 'virement sortant', classe: 'bg-orange-100 text-orange-800' },
  stripe: { label: 'remboursement Stripe', classe: 'bg-orange-100 text-orange-800' },
};

/**
 * Ce qu'un solde résiduel veut DIRE, et ce qui le résoudrait.
 *
 * ## Le défaut que ce type existe pour corriger
 *
 * Signalé le 10 septembre 2026 sur `CDV-TEST-2602043` : l'écran affichait
 * « Annulée » **et** « réclame encore 1,50 € », tout en présentant le bouton
 * de note de crédit en gris. Une dette annoncée, et la seule action qui
 * l'effacerait qui a l'air interdite. L'incohérence était réelle.
 *
 * Elle venait d'une confusion de ma part : `outstanding_cents` est
 * arithmétiquement juste — brut moins notes moins reçu — mais il ne dit pas
 * **de qui** l'argent est réclamé. Sur une facture annulée, la réponse est
 * « de personne » : la décision d'annuler l'a déjà éteinte pour la famille.
 * Le §0 bis de la passation le dit en une phrase : « Aucune de ces lignes
 * n'appelle une action commerciale. »
 *
 * ## Les deux populations d'annulées, mesurées le 10 septembre 2026
 *
 * | | factures | résiduel | ce qui le résout |
 * | --- | ---: | ---: | --- |
 * | annulées, document parti | **10** | **1 093,00 €** | une note de crédit |
 * | annulées avant tout envoi | **1** | **1,50 €** | rien |
 *
 * Les dix sont de juin 2025 : le document est parti, la famille a reçu une
 * facture, et il faut une pièce comptable pour l'effacer — c'est le stock que
 * la règle D1 doit reprendre. La onzième est un panier abandonné annulé par
 * `cancel_order` dans les 30 minutes : aucun document n'a jamais été produit
 * ni envoyé, donc **l'annulation se suffit à elle-même** (§5.6). Émettre une
 * note pour elle consommerait un numéro afin d'annuler une pièce que personne
 * n'a reçue.
 *
 * Aucune des deux ne « réclame » quoi que ce soit à une famille. Les
 * distinguer par un seul mot était l'erreur ; elles ont maintenant chacune le
 * leur, et chacune son filtre — le §5.6 interdit de faire disparaître la
 * contradiction, pas de la nommer correctement.
 */
export type Reclamation =
  /** Rien ne reste : la facture est soldée, créditée, ou sans reste. */
  | 'aucune'
  /** La famille doit encore de l'argent. C'est la seule qui appelle un rappel. */
  | 'due'
  /** Annulée, document parti : une note de crédit doit effacer la dette. */
  | 'note_a_emettre'
  /** Annulée avant tout envoi : rien à réclamer, rien à créditer. */
  | 'annulation_seule';

/** Ce que le solde résiduel d'une facture signifie réellement. */
export function reclamation(
  f: Pick<LigneFacture, 'settlement' | 'resteCents' | 'documentDelivre'>,
): Reclamation {
  if (f.resteCents <= 0) return 'aucune';
  if (f.settlement !== 'cancelled') return 'due';
  return f.documentDelivre ? 'note_a_emettre' : 'annulation_seule';
}

/** Comment chaque cas s'annonce sur la ligne, et ce qu'il faut en comprendre. */
/**
 * Ce qu'une facture trouvée par son numéro peut encore recevoir, en une ligne.
 *
 * ## Pourquoi cette phrase existe
 *
 * Signalé le 18 septembre 2026, sur un virement de 40,00 € dont la facture
 * citée était annulée : « pourquoi ne puis-je pas le mettre en provision ? La
 * facture a été annulée mais on a quand même reçu les sous. »
 *
 * La recherche manuelle écartait les factures annulées. Taper `CDV-WBK-2600675`
 * rendait donc « Rien trouvé pour cette recherche » alors que la facture
 * existe — la famille restait introuvable, donc sans suggestion, donc sans
 * destination possible, et le seul geste offert était « hors périmètre », qui
 * est FAUX : l'argent est arrivé et appartient à la famille.
 *
 * **Une facture qui ne peut rien recevoir n'est pas une facture absente.**
 * C'est une réponse, et souvent LA réponse : elle dit pourquoi le paiement n'a
 * pas trouvé de destination, et elle nomme la famille à qui l'argent revient.
 * Ce qu'il faut, c'est dire son état.
 */
export function phraseDeFactureTrouvee(
  f: { invoiceNumber: string; outstandingCents: number; settlement: string },
): string {
  const trouvee = `facture ${f.invoiceNumber} trouvée par son numéro`;
  if (f.settlement === 'cancelled') {
    return `${trouvee}, mais ANNULÉE : elle ne peut rien recevoir. Le solde peut aller en provision.`;
  }
  if (f.settlement === 'credited') {
    return `${trouvee}, mais entièrement couverte par des notes de crédit : elle ne réclame plus rien.`;
  }
  if (f.outstandingCents === 0) return `${trouvee}, mais déjà entièrement réglée`;
  return trouvee;
}

export const RECLAMATIONS: Record<
  Exclude<Reclamation, 'aucune' | 'due'>,
  { marque: string; classe: string; phrase: string }
> = {
  note_a_emettre: {
    marque: 'note de crédit à émettre',
    classe: 'text-amber-800',
    phrase:
      'Annulée, et le document est parti. La famille ne doit rien, mais le solde reste inscrit : '
      + 'c’est une note de crédit qui doit en porter la trace comptable.',
  },
  annulation_seule: {
    marque: 'annulée avant tout envoi',
    classe: 'text-gray-500',
    phrase:
      'Annulée avant qu’aucun document ne soit produit ni envoyé — un panier abandonné. '
      + 'L’annulation se suffit à elle-même : le solde résiduel est une trace arithmétique, '
      + 'pas une créance, et aucune note de crédit n’est à émettre.',
  },
};

/**
 * Pourquoi le bouton « note de crédit » est indisponible, ou `null` s'il l'est.
 *
 * `create_credit_note` refuse **toute** facture déjà annulée :
 *
 * ```sql
 * IF v_invoice.status = 'cancelled' THEN
 *   RAISE EXCEPTION 'la facture % est deja annulee', v_invoice.invoice_number
 * ```
 *
 * C'est ce refus-là qui empêchait d'agir, pas une règle d'écran. Le bouton le
 * dit donc, et il est réellement désactivé : il était gris **et cliquable**,
 * ce qui est le pire des deux — il a l'air interdit sans expliquer, et il
 * mène à une exception SQL brute si on insiste.
 *
 * Conséquence à ne pas perdre de vue : ce même refus bloque la reprise
 * rétroactive des dix factures de juin 2025. La règle D1 fonctionne vers
 * l'avant — la note précède l'annulation, et le déclencheur passe la facture
 * à `cancelled` — mais le stock déjà annulé ne peut pas être crédité en
 * l'état.
 */
export function empechementNoteDeCredit(
  f: Pick<LigneFacture, 'settlement' | 'documentDelivre'>,
): string | null {
  if (f.settlement !== 'cancelled') return null;
  return f.documentDelivre
    ? 'Facture déjà annulée : create_credit_note la refuse. La reprise du stock de juin 2025 '
      + 'demande de trancher ce point en base.'
    : 'Annulée avant tout envoi : aucun document n’a été produit, donc il n’y a rien à créditer.';
}
